import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/HomeView.vue'
import Login from '../views/LoginView.vue'
import RouterWrapper from '@/layouts/RouterWrapper.vue'
import { useAuthStore } from '@/stores/auth'
import { saveIntendedUrl } from '@/lib/redirectAfterLogin'
import QueryView from '../views/QueryView.vue'

const routes = [
  {
    path: '/',
    redirect: '/trainers/all',
    meta: { requiresAuth: true },
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('../views/auth/ForgotPasswordView.vue'),
  },
  {
    path: '/reset-password/:uidb64/:token',
    name: 'ResetPassword',
    component: () => import('../views/auth/ResetPasswordView.vue'),
    props: true,
  },
  {
    path: '/signup',
    name: 'SignUp',
    component: Home,
  },
  {
    path: '/settings',
    name: 'Settings',
    component: Home,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFoundView.vue'),
  },
  {
    path: '/',
    component: () => import('../layouts/DashboardLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'query',
        name: 'QueryView',
        component: QueryView,
      },
      {
        path: 'lookup-tables',
        name: 'LookupTables',
        component: () => import('../views/LookupTablesView.vue'),
        meta: { permission: 'lookup.view_lookupcountries' },
      },
      {
        path: 'trainers',
        redirect: '/trainers/all',
        component: RouterWrapper,
        children: [
          {
            path: 'all',
            name: 'All Trainers',
            component: () => import('../views/trainers/AllView.vue'),
          },
          {
            path: 'certification-status',
            name: 'TrainersCertified',
            component: () => import('../views/trainers/CertifiedView.vue'),
            meta: { permission: 'profiles.view_trainer_certification_status' },
          },
          {
            path: 'course-list',
            name: 'TrainersCourseList',
            component: () => import('../views/courses/ListView.vue'),
            props: { courseType: 'trainers' },
          },
          {
            path: 'course-list/:id',
            name: 'TrainersCourseDetails',
            component: () => import('../views/courses/DetailView.vue'),
            props: { courseType: 'trainers' },
          },
          {
            path: 'statistics',
            name: 'TrainersStatistics',
            component: () => import('../views/StatisticsView.vue'),
            props: { context: 'trainers' },
            meta: { permission: 'profiles.view_statistics_dashboard' },
          },
          {
            path: 'add',
            name: 'Add Trainer',
            component: () => import('../views/trainers/AddView.vue'),
            meta: { permission: 'profiles.add_profile' },
          },
          {
            path: ':id',
            name: 'Trainer Details',
            component: () => import('../views/trainers/TrainerView.vue'),
          },
        ],
      },
      {
        path: 'participants',
        redirect: '/participants/all',
        component: RouterWrapper,
        children: [
          {
            path: 'all',
            name: 'ParticipantsAll',
            component: () => import('../views/participants/AllView.vue'),
          },
          {
            path: 'course-list',
            name: 'ParticipantsCourseList',
            component: () => import('../views/courses/ListView.vue'),
            props: { courseType: 'participants' },
          },
          {
            path: 'course-list/:id',
            name: 'ParticipantsCourseDetails',
            component: () => import('../views/courses/DetailView.vue'),
            props: { courseType: 'participants' },
          },
          {
            path: 'statistics',
            name: 'ParticipantsStatistics',
            component: () => import('../views/StatisticsView.vue'),
            props: { context: 'participants' },
            meta: { permission: 'profiles.view_statistics_dashboard' },
          },
          {
            path: ':id',
            name: 'Participant Details',
            component: () => import('../views/participants/DetailView.vue'),
          },
        ],
      },
      {
        path: 'course/upload',
        name: 'CourseUpload',
        component: () => import('../views/courses/UploadView.vue'),
        meta: { permission: 'course.import_course_data' },
      },
      {
        path: 'admin/user-permissions',
        name: 'UserPermissions',
        component: () => import('../views/admin/UserPermissionsView.vue'),
        meta: { permission: 'profiles.manage_user_permissions', requiresStaff: true },
      },
      {
        path: 'admin/create-user',
        name: 'CreateUser',
        component: () => import('../views/admin/CreateUserView.vue'),
        meta: { permission: 'profiles.manage_user_permissions', requiresStaff: true },
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

let isRefreshing = false

router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  if (!auth.isAuthenticated) {
    await auth.restoreSession()
  }

  if (requiresAuth && !auth.isAuthenticated) {
    saveIntendedUrl(to.fullPath)
    return next({ name: 'Login' })
  }

  // Redirect authenticated users away from /login — unless they arrived with an
  // external-access token in the query string, in which case LoginView handles
  // the "already authenticated" case itself (skips redemption, shows a notice).
  if (to.name === 'Login' && auth.isAuthenticated && !to.query.token) {
    return next('/')
  }

  if (auth.isAuthenticated && !isRefreshing) {
    isRefreshing = true
    try {
      await auth.refreshToken()
    } catch (error) {
      console.log(error)
      saveIntendedUrl(to.fullPath)
      return next({ name: 'Login' })
    } finally {
      isRefreshing = false
    }
  }

  // Staff Check (Django is_staff) — for back-office tools mirroring admin access.
  if (to.meta.requiresStaff && !auth.user?.isStaff) {
    return next({ name: 'NotFound' })
  }

  // Permission Check
  if (to.meta.permission) {
    const requiredPermission = to.meta.permission as string
    if (!auth.hasPermission(requiredPermission)) {
      // Redirect to NotFound if permission is missing
      return next({ name: 'NotFound' })
    }
  }

  next()
})

export default router
